import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaMinus,
  FaTrash,
  FaArrowLeft,
  FaReceipt,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import {
  placeOrder as placeOrderApi,
  getRestaurantProfile,
  fetchOrders,
} from "../utils/api";
import CartInvoice from "../components/CartInvoice";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Helper to get full image URL (copied from CustomerTable.js)
const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;
function getFullImageUrl(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http")) return imageUrl;
  const cleanUrl = imageUrl.replace(/^\/?.*uploads\//, "");
  return `${BASE_URL}/uploads/${cleanUrl}`;
}

// Helper to extract real table number from tableId
function getRealTableNumber(tableId) {
  if (!tableId) return "N/A";

  // If tableId is already a simple number, return it
  if (/^\d+$/.test(tableId)) {
    return tableId;
  }

  // If tableId contains "Table" followed by a number, extract it
  const tableMatch = tableId.match(/Table\s*(\d+)/i);
  if (tableMatch) {
    return tableMatch[1];
  }

  // If tableId is a long alphanumeric string, try to extract a number from it
  const numberMatch = tableId.match(/(\d{1,4})/);
  if (numberMatch) {
    return numberMatch[1];
  }

  // If no number found, return the original tableId
  return tableId;
}

export default function Cart() {
  const query = useQuery();
  const restaurantId = query.get("restaurantId");
  const tableId = query.get("tableId");
  const roomId = query.get("roomId");
  const [cart, setCart] = useState([]);
  const [placingOrder, setPlacingOrder] = useState(false);
  //const [showInvoice, setShowInvoice] = useState(false);
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);

  const [showInvoice, setShowInvoice] = useState(false);

  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const navigate = useNavigate();

  // Debug log for cart key and value
  const cartKey = roomId
    ? `room_cart_${restaurantId}_${roomId}`
    : `cart_${restaurantId}_${tableId}`;
  const infoKey = roomId ? `room_info_${restaurantId}_${roomId}` : null;

  // Fetch restaurant details
  useEffect(() => {
    if (restaurantId) {
      getRestaurantProfile(restaurantId)
        .then((data) => {
          setRestaurantName(data.name || "RESTAURANT");
        })
        .catch((err) => {
          console.error("Failed to load restaurant profile", err);
          setRestaurantName("RESTAURANT");
        });
    }
  }, [restaurantId]);

  useEffect(() => {
    if (restaurantId && (tableId || roomId)) {
      console.log("Cart.js: Loading cart with key:", cartKey);
      const stored = localStorage.getItem(cartKey);
      console.log("Cart.js: Loaded value:", stored);
      setCart(stored ? JSON.parse(stored) : []);
      if (roomId && infoKey) {
        const info = localStorage.getItem(infoKey);
        if (info) {
          const { roomNumber, phoneNumber } = JSON.parse(info);
          setRoomNumber(roomNumber || "");
          setPhoneNumber(phoneNumber || "");
        }
      }
    }
  }, [restaurantId, tableId, roomId, cartKey, infoKey]);

  useEffect(() => {
    if (restaurantId && (tableId || roomId) && cart.length > 0) {
      localStorage.setItem(cartKey, JSON.stringify(cart));
    }
    // Do NOT save empty cart to localStorage on mount
  }, [cart, restaurantId, tableId, roomId, cartKey]);

  function incrementQty(itemId) {
    setCart((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, qty: i.qty + 1 } : i))
    );
  }

  function decrementQty(itemId) {
    setCart((prev) => {
      const found = prev.find((i) => i.id === itemId);
      if (found && found.qty === 1) {
        return prev.filter((i) => i.id !== itemId);
      } else {
        return prev.map((i) =>
          i.id === itemId ? { ...i, qty: i.qty - 1 } : i
        );
      }
    });
  }

  function removeFromCart(itemId) {
    setCart((prev) => prev.filter((i) => i.id !== itemId));
  }

  function handleBack() {
    navigate(-1);
  }

  function handleCloseInvoice() {
    setShowInvoice(false);
    // If we're not already showing the success screen, show it
    if (!showOrderSuccess) {
      setShowOrderSuccess(true);
    }
  }

  function handleDownloadInvoice() {
    if (!orderDetails) return;

    // Create a formatted bill content
    const billContent = generateBillContent();

    // Create blob and download
    const blob = new Blob([billContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `EasiDine_Bill_${orderDetails.orderId}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  function generateBillContent() {
    if (!orderDetails) return "";

    const now = new Date();
    const dateStr = now.toLocaleDateString("en-GB");
    const timeStr = now.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    let bill = "";
    bill += "=".repeat(40) + "\n";
    bill += `           ${restaurantName}\n`;
    bill += "=".repeat(40) + "\n";

    bill += `Bill No: ${orderDetails.billNumber}\n`;
    bill += `Date: ${dateStr}\n`;
    bill += `Table: ${orderDetails.tableName || orderDetails.tableId}\n`;

    bill += `Bill No: ${orderDetails.orderId}\n`;
    bill += `Date: ${dateStr}\n`;
    bill += `Table: ${orderDetails.tableId || "N/A"}\n`;

    bill += `Time: ${timeStr}\n`;
    bill += "-".repeat(40) + "\n";
    bill += "Item                    Qty  Rate    Amt\n";
    bill += "-".repeat(40) + "\n";

    orderDetails.items.forEach((item) => {
      const itemName = item.name.padEnd(20);
      const qty = item.qty.toString().padStart(3);
      const rate = item.price.toString().padStart(6);
      const amount = (item.qty * item.price).toFixed(2).padStart(8);
      bill += `${itemName} ${qty} ${rate} ${amount}\n`;
    });

    bill += "-".repeat(40) + "\n";
    bill += `Grand Total:${" ".repeat(20)}${orderDetails.total.toFixed(2)}\n`;
    bill += "=".repeat(40) + "\n";
    bill += "        Thank You, Please Visit again\n";
    bill += "=".repeat(40) + "\n";
    bill += "           Powered by EasiDine\n";

    return bill;
  }

  async function placeOrder() {
    setPlacingOrder(true);
    setError("");
    try {
      const orderData = {
        restaurantId,
        tableId: tableId || undefined,
        // prefer room label when roomId present
        tableName: roomId
          ? `Room ${roomNumber}`
          : tableId
          ? `Table ${getRealTableNumber(tableId)}`
          : undefined,
        items: cart,
        roomId: roomId || undefined,
        roomNumber: roomId ? roomNumber : undefined,
        phoneNumber: roomId ? phoneNumber : undefined,
        source: "customer", // Mark as customer order
        discountPercent: 0, // No discount for customer orders
      };

      const response = await placeOrderApi(orderData);
      console.log("Place order response:", response);

      // Try to fetch server-side order to get authoritative tableName and billNumber
      let serverOrder = null;
      if (response?.orderId) {
        try {
          const orders = await fetchOrders(restaurantId);
          serverOrder = orders.find((o) => o.id === response.orderId) || null;
          console.log("Found server order:", serverOrder);
        } catch (err) {
          console.warn("Failed to fetch created order from server:", err);
        }
      }

      // Store order details for invoice
      const finalItems = serverOrder?.items || cart;
      const subtotal =
        typeof serverOrder?.total === "number"
          ? serverOrder.total
          : cart.reduce((sum, i) => sum + Number(i.price) * i.qty, 0);
      const orderInfo = {
        orderId:
          serverOrder?.id ||
          response?.orderId ||
          `${Date.now().toString().slice(-5)}`,
        billNumber:
          serverOrder?.billNumber ??
          response?.billNumber ??
          (response?.orderId
            ? response.orderId.slice(-5)
            : `${Date.now().toString().slice(-5)}`),
        timestamp:
          serverOrder?.createdAt && serverOrder.createdAt._seconds
            ? new Date(serverOrder.createdAt._seconds * 1000).toISOString()
            : new Date().toISOString(),
        items: finalItems,
        subtotal,
        tax: Math.round(subtotal * 0.05 * 100) / 100,
        total: Math.round(subtotal * 1.05 * 100) / 100,
        // Prefer server-provided tableName (authoritative). Fallback to what we passed, and include room fallback.
        tableId: serverOrder?.tableId || tableId || (roomId ? `room-${roomId}` : undefined),
        tableName:
          serverOrder?.tableName ||
          orderData.tableName ||
          (roomId ? `Room ${roomNumber}` : tableId ? `Table ${getRealTableNumber(tableId)}` : undefined),
      };

      setOrderDetails(orderInfo);
      setShowInvoice(true); // Show invoice first
      setCart([]);
      localStorage.removeItem(cartKey);
      if (roomId && infoKey) localStorage.removeItem(infoKey);
    } catch (err) {
      setError("Failed to place order");
    }
    setPlacingOrder(false);
  }

  // Show order success message
  if (showOrderSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4 text-center"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 0.6 }}
            className="text-6xl mb-4 text-green-500"
          >
            ✓
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Order Placed!
          </h2>
          <p className="text-gray-600 mb-6">
            Thank you for your order. Your food will be served shortly.
          </p>

          <div className="flex flex-col gap-3">
            <motion.button
              onClick={() => setShowInvoice(true)}
              className="px-6 py-3 bg-gradient-to-r from-rose-400 to-amber-400 text-white rounded-full font-medium hover:shadow-md transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaReceipt className="text-lg" />
              View Invoice
            </motion.button>

            <motion.button
              onClick={handleBack}
              className="px-6 py-3 bg-gradient-to-r from-rose-400 to-amber-400 text-white rounded-full font-medium hover:shadow-md transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-lg" />
              Back to Menu
            </motion.button>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Powered by{" "}
              <span className="font-bold text-rose-400">EasiDine</span>
            </p>
          </div>
        </motion.div>

        {/* Invoice Modal for success screen */}
        {showInvoice && orderDetails && (
          <CartInvoice
            orderDetails={orderDetails}
            restaurantName={restaurantName}
            onClose={() => setShowInvoice(false)}
            onDownload={handleDownloadInvoice}
          />
        )}
      </div>
    );
  }

  // Show invoice modal when order is placed
  if (showInvoice && orderDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center p-4">
        <CartInvoice
          orderDetails={orderDetails}
          restaurantName={restaurantName}
          onClose={handleCloseInvoice}
          onDownload={handleDownloadInvoice}
        />
      </div>
    );
  }

  if (!restaurantId || (!tableId && !roomId)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="text-3xl mb-4 text-red-400">⚠️</div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">
            Missing Information
          </h2>
          <p className="text-gray-600 mb-4">
            Restaurant or Table/Room information is missing. Please return to
            the menu and try again.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-gradient-to-r from-rose-400 to-amber-400 text-white rounded-full font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex flex-col items-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden mt-4"
      >
        <div className="p-4">
          {/* Room/Phone Info */}
          {roomId && (
            <div className="mb-4 flex flex-col gap-2">
              <div className="text-sm text-gray-700 font-semibold">
                Room Number: <span className="font-bold">{roomNumber}</span>
              </div>
              <div className="text-sm text-gray-700 font-semibold">
                Phone Number: <span className="font-bold">{phoneNumber}</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 mb-6">
            <motion.button
              className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors"
              onClick={handleBack}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaArrowLeft className="text-sm" />
            </motion.button>
            <h2 className="text-xl font-bold flex-1 text-center text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
              Your Cart
            </h2>
          </div>

          {cart.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center py-12"
            >
              <div className="text-gray-400 text-lg mb-2">
                Your cart is empty
              </div>
              <motion.button
                onClick={handleBack}
                className="px-4 py-2 bg-gradient-to-r from-rose-100 to-amber-100 text-rose-600 rounded-full text-sm font-medium mt-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Browse Menu
              </motion.button>
            </motion.div>
          ) : (
            <div className="flex flex-col gap-4">
              <AnimatePresence>
                {cart.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-rose-50 to-amber-50 rounded-lg"
                    whileHover={{ scale: 1.01 }}
                  >
                    {item.imageUrl && (
                      <motion.div className="w-14 h-14 rounded-lg overflow-hidden shadow-sm">
                        <img
                          src={getFullImageUrl(item.imageUrl)}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-800 truncate">
                        {item.name}
                      </div>
                      <div className="text-sm font-bold text-rose-500">
                        ₹{item.price}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.button
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-white text-rose-500 shadow-sm hover:shadow-md transition-all"
                        onClick={() => decrementQty(item.id)}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaMinus className="text-xs" />
                      </motion.button>
                      <motion.span
                        key={`qty-${item.id}-${item.qty}`}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        className="px-1 min-w-[20px] text-center text-sm font-medium"
                      >
                        {item.qty}
                      </motion.span>
                      <motion.button
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-white text-rose-500 shadow-sm hover:shadow-md transition-all"
                        onClick={() => incrementQty(item.id)}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaPlus className="text-xs" />
                      </motion.button>
                    </div>
                    <motion.button
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-white text-red-500 shadow-sm hover:shadow-md transition-all ml-2"
                      onClick={() => removeFromCart(item.id)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaTrash className="text-xs" />
                    </motion.button>
                  </motion.div>
                ))}
              </AnimatePresence>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mt-4 pt-4 border-t border-rose-100"
              >
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">Subtotal:</span>
                  <span className="font-bold text-gray-800">
                    ₹{cart.reduce((sum, i) => sum + Number(i.price) * i.qty, 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-gray-500">Tax (5%):</span>
                  <span className="text-sm text-gray-500">
                    ₹
                    {(
                      cart.reduce(
                        (sum, i) => sum + Number(i.price) * i.qty,
                        0
                      ) * 0.05
                    ).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-rose-100">
                  <span className="font-bold text-lg text-gray-800">
                    Total:
                  </span>
                  <span className="font-bold text-lg text-rose-600">
                    ₹
                    {(
                      cart.reduce(
                        (sum, i) => sum + Number(i.price) * i.qty,
                        0
                      ) * 1.05
                    ).toFixed(2)}
                  </span>
                </div>
              </motion.div>

              <motion.button
                className={`w-full py-3 rounded-full text-lg font-medium mt-6 ${
                  placingOrder
                    ? "bg-amber-400"
                    : "bg-gradient-to-r from-rose-500 to-amber-500"
                } text-white shadow-lg hover:shadow-xl transition-all`}
                onClick={placeOrder}
                disabled={placingOrder}
                whileHover={!placingOrder ? { scale: 1.02 } : {}}
                whileTap={!placingOrder ? { scale: 0.98 } : {}}
              >
                {placingOrder ? (
                  <motion.span
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    Placing Order...
                  </motion.span>
                ) : (
                  "Place Order"
                )}
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>

      <motion.div
        className="text-center text-xs text-gray-400 mt-8 mb-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        Powered by <span className="font-bold text-rose-400">EasiDine</span>
      </motion.div>

      {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
    </div>
  );
}

// Print styles for invoice
const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    .invoice-print, .invoice-print * {
      visibility: visible;
    }
    .invoice-print {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      background: white;
      padding: 20px;
      font-family: 'Courier New', monospace;
    }
    .no-print {
      display: none !important;
    }
    .invoice-print .grid {
      display: table;
      width: 100%;
    }
    .invoice-print .grid > div {
      display: table-cell;
      vertical-align: top;
    }
  }
`;

// Add print styles to document head
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = printStyles;
  document.head.appendChild(styleElement);
}
